import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // TODO: Send to error tracking service
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-mesh p-4">
          <div className="text-center max-w-md animate-scale-in">
            <div className="w-16 h-16 rounded-2xl bg-destructive/8 flex items-center justify-center mx-auto mb-6 shadow-[0_0_24px_rgba(220,42,74,0.1)]">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-2 tracking-tight">
              {i18n.t('errorBoundary.title')}
            </h1>
            
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              {i18n.t('errorBoundary.description')}
            </p>

            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-2xl font-semibold text-sm shadow-[0_2px_8px_rgba(80,11,77,0.25)] hover:shadow-[0_4px_16px_rgba(80,11,77,0.35)] hover:brightness-110 transition-all duration-200 active:scale-[0.97]"
            >
              <RefreshCw className="w-4 h-4" />
              {i18n.t('errorBoundary.tryAgain')}
            </button>

            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-8 p-4 bg-muted/50 border border-border/40 rounded-xl text-left text-xs overflow-auto max-h-48 text-muted-foreground">
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
